import { useState, useEffect } from 'react';
import { NavBar } from './NavBar.jsx';
import { HelpAssistant } from './HelpAssistant.jsx';
import { PasswordInput } from './PasswordInput.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthApi, ColaboradoresApi } from '../api/client.js';
import { useToast } from './ToastProvider.jsx';
import { Modal } from './Modal.jsx';

export function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobile, setMobile] = useState(false);
  const [modalSenhaOpen, setModalSenhaOpen] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
  const { auth, logout } = useAuth();
  const toast = useToast();
  const perfilNormalizado = String(auth?.usuario?.perfil || '').trim().toUpperCase();
  const isAuthReady = Boolean(perfilNormalizado);
  const isColaborador = perfilNormalizado === 'COLABORADOR';

  // Carrega (lazy) dados de colaborador para setor/cargo se existir usuário
  const usuarioId = auth?.usuario?.id;
  const { data: colaboradoresData } = useQuery({
    queryKey:['colab-header', usuarioId],
    enabled: isAuthReady && !isColaborador && !!usuarioId,
    queryFn: async () => {
      // Reutilizando lista e filtrando localmente para evitar criar novo endpoint.
      const resp = await ColaboradoresApi.listar();
      const arr = Array.isArray(resp) ? resp : (resp?.data || []);
      return arr.find(c => c.usuario?.id === usuarioId) || null;
    }
  });

  useEffect(() => {
    const onResize = () => {
      const isMobile = window.innerWidth < 900; // breakpoint
      setMobile(isMobile);
      setSidebarOpen(!isMobile); // fecha por padrão em telas pequenas
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const alterarSenha = useMutation({
    mutationFn: () => AuthApi.alterarSenha({ senhaAtual, novaSenha }),
    onSuccess: () => {
      setModalSenhaOpen(false);
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarNovaSenha('');
      toast.success('Senha alterada com sucesso. Faça login novamente.');
      logout();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message || 'Erro ao alterar senha');
    },
  });

  function fecharModalSenha() {
    setModalSenhaOpen(false);
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarNovaSenha('');
  }

  function confirmarAlteracaoSenha() {
    if (novaSenha !== confirmarNovaSenha) {
      toast.error('A confirmação de senha não confere');
      return;
    }
    alterarSenha.mutate();
  }

  return (
    <div className={"layout" + (sidebarOpen ? ' sidebar-open' : ' sidebar-closed') + (mobile ? ' mobile' : '')}>
      <NavBar collapsed={!sidebarOpen && !mobile} visible={sidebarOpen || !mobile} onCloseMobile={()=> setSidebarOpen(false)} onToggleCollapse={()=> setSidebarOpen(o=>!o)} mobile={mobile} />
      <div style={{flex:1, display:'flex', flexDirection:'column', minWidth:0}}>
        <HeaderTitulo
          onToggleSidebar={() => setSidebarOpen(o=>!o)}
          sidebarOpen={sidebarOpen}
          mobile={mobile}
          colaborador={colaboradoresData}
          usuario={auth?.usuario}
          onOpenChangePassword={() => setModalSenhaOpen(true)}
        />
        <main className="content">{children}</main>
        <footer className="site-footer">
          <span>Reciclagem Capital © Todos os direitos reservados.</span>
        </footer>
      </div>
      {mobile && sidebarOpen && <div className="backdrop" onClick={()=>setSidebarOpen(false)} />}
      <HelpAssistant />
      <Modal
        open={modalSenhaOpen}
        title="Alterar minha senha"
        onClose={fecharModalSenha}
        actions={
          <>
            <button className="btn" onClick={fecharModalSenha} style={{ background: '#6c757d' }}>
              Cancelar
            </button>
            <button className="btn" onClick={confirmarAlteracaoSenha} disabled={alterarSenha.isPending}>
              Salvar senha
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
          <PasswordInput
            label="Senha atual"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            style={{ width: '100%' }}
          />
          <PasswordInput
            label="Nova senha"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            style={{ width: '100%' }}
          />
          <PasswordInput
            label="Confirmar nova senha"
            value={confirmarNovaSenha}
            onChange={(e) => setConfirmarNovaSenha(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </Modal>
    </div>
  );
}

function HeaderTitulo({ onToggleSidebar, sidebarOpen, mobile, colaborador, usuario, onOpenChangePassword }) {
  const { logout } = useAuth();
  const displayNome = (usuario?.nome || usuario?.email || '').replace(/\s+/g,' ').trim();
  return (
    <header className="app-header">
      <div className="header-left">
        {mobile && (
          <button className="icon-btn" onClick={onToggleSidebar} aria-label="Alternar menu">
            <span className="hamburger" data-open={sidebarOpen}></span>
          </button>
        )}
        <div className="titles">
          <span className="app-title">Reciclagem Capital</span>
          <span className="app-subtitle">Sistema de Bônus e Métricas</span>
        </div>
      </div>
      <div className="header-userinfo" style={{display:'flex', alignItems:'center', gap:'1.2rem'}}>
        {usuario && (
            <div className="user-box user-box-inline" style={{padding:'.25rem .4rem'}}>
              <span className="user-inline"><strong>Usuário:</strong> {displayNome}</span>
              <span className="sep">•</span>
              <span className="user-inline"><strong>Perfil:</strong> {usuario.perfil}</span>
            </div>
        )}
        <span className="logout-text" role="button" tabIndex={0}
          onClick={onOpenChangePassword}
          onKeyDown={(e)=> { if(e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenChangePassword(); } }}
        >Alterar senha</span>
        <span className="logout-text" role="button" tabIndex={0}
          onClick={logout}
          onKeyDown={(e)=> { if(e.key === 'Enter' || e.key === ' ') { e.preventDefault(); logout(); } }}
        >Sair</span>
      </div>
    </header>
  );
}
